import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { auditService } from '../services/api';
import { ROLES } from '../utils/rbac';
import { BarChart3 as BarChart3Icon, Hourglass as HourglassIcon } from 'lucide-react';

/**
 * AuditLogs Component with Pagination
 * Based on AUDIT_LOGS_API_INTEGRATION_GUIDE.md
 * 
 * Features:
 * - 100 logs per page (configurable)
 * - Filter by user, action, date range
 * - Role-based access control
 * - Full metadata viewing in modal
 * - Page navigation with page numbers
 */

const Colors = {
  primary: '#1A3E2A',
  secondary: '#5F6B61',
  success: '#1A3E2A',
  danger: '#A13F2F',
  warning: '#B68235',
  info: '#1A3E2A',
  white: '#ffffff',
  light: '#F5F3EB',
  border: '#E3DFD3',
  text: '#1A3E2A',
  textLight: '#5F6B61',
  hover: '#EEEBE1'
};

function AuditLogs({ currentUser }) {
  // State
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showMetadataModal, setShowMetadataModal] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 100; // As per API docs

  // Filter state
  const [filters, setFilters] = useState({
    userId: '',
    action: '',
    startDate: '',
    endDate: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({});

  // Load audit logs with pagination
  const loadAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        ...appliedFilters
      };

      const response = await auditService.getAll(params);
      
      // Handle different response formats
      let auditData = [];
      let paginationData = {};

      if (response?.data) {
        // New format: { data: [...], pagination: {...} }
        auditData = Array.isArray(response.data) ? response.data : [];
        paginationData = response.pagination || {};
      } else if (Array.isArray(response)) {
        // Direct array format
        auditData = response;
      }

      setLogs(auditData);
      
      // Set pagination info
      if (paginationData.totalPages) {
        setTotalPages(paginationData.totalPages);
        setTotalItems(paginationData.totalItems || 0);
      } else {
        // Fallback if pagination not provided
        setTotalPages(1);
        setTotalItems(auditData.length);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Failed to load audit logs');
      setLogs([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, appliedFilters]);

  // Load logs when page or filters change
  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters and reset to page 1
  const handleApplyFilters = () => {
    const newFilters = {};
    
    if (filters.userId?.trim()) {
      newFilters.userId = filters.userId.trim();
    }
    if (filters.action?.trim()) {
      newFilters.action = filters.action.trim();
    }
    if (filters.startDate) {
      newFilters.startDate = new Date(filters.startDate).toISOString();
    }
    if (filters.endDate) {
      // Set to end of day
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      newFilters.endDate = endDate.toISOString();
    }

    setAppliedFilters(newFilters);
    setCurrentPage(1); // Reset to first page
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      userId: '',
      action: '',
      startDate: '',
      endDate: ''
    });
    setAppliedFilters({});
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  // Get page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 7;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // View details modal
  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowMetadataModal(true);
  };

  const handleCloseModal = () => {
    setShowMetadataModal(false);
    setSelectedLog(null);
  };

  // Get action icon
  const getActionIcon = (action) => {
    const icons = {
      USER_CREATED: '',
      USER_ROLE_CHANGED: '',
      USER_PASSWORD_RESET: '',
      USER_PASSWORD_CHANGED: '',
      USER_PROFILE_UPDATED: '',
      USER_VERIFIED: '',
      USER_UNVERIFIED: '',
      USER_DELETED: '',
      BOOKING_CREATED: '',
      BOOKING_UPDATED: '',
      BOOKING_CANCELLED: '',
      BOOKING_CONFIRMED: '',
      BOOKING_CHECKED_IN: '',
      BOOKING_CHECKED_OUT: '',
      PAYMENT_RECEIVED: '',
      PAYMENT_REFUNDED: '',
      ROOM_UPDATED: '',
      ROOM_CREATED: '',
      ITEM_CREATED: '',
      ITEM_UPDATED: '',
      INVENTORY_ADJUSTED: ''
    };
    return icons[action] || '';
  };

  // Get role badge color
  const getRoleColor = (role) => {
    const colors = {
      SUPERADMIN: '#8E3628',
      PROPERTYADMIN: '#975534',
      STAFF: '#163626',
      ACCOUNTS: '#163626',
      INVENTORY: '#975534',
      SERVICE: '#975534',
      GUEST: '#5F6B61'
    };
    return colors[role] || Colors.secondary;
  };

  // Format metadata preview
  const formatMetadataPreview = (metadata) => {
    if (!metadata) return 'N/A';
    
    try {
      const data = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      const details = [];
      
      if (data.bookingId) details.push(`Booking: ...${data.bookingId.slice(-8)}`);
      if (data.roomId) details.push(`Room: ...${data.roomId.slice(-8)}`);
      if (data.bedId) details.push(`Bed: ...${data.bedId.slice(-8)}`);
      if (data.userId) details.push(`User: ...${data.userId.slice(-8)}`);
      if (data.targetUserId) details.push(`Target: ...${data.targetUserId.slice(-8)}`);
      
      const previewText = details.length > 0 ? details.slice(0, 2).join(', ') : 'System action';
      const hasMore = details.length > 2 || Object.keys(data).length > 4;
      
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{previewText}</span>
          {hasMore && <span style={{ color: Colors.primary, fontSize: '12px' }}>+more</span>}
        </div>
      );
    } catch (e) {
      const metaString = String(metadata);
      return metaString.substring(0, 50) + (metaString.length > 50 ? '...' : '');
    }
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = getPageNumbers();

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        padding: '16px',
        background: Colors.white,
        border: `1px solid ${Colors.border}`,
        borderRadius: '8px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        {/* Page Info */}
        <div style={{ color: Colors.textLight, fontSize: '14px' }}>
          Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems} logs
        </div>

        {/* Pagination Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Previous Button */}
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              border: `1px solid ${Colors.border}`,
              borderRadius: '6px',
              background: currentPage === 1 ? Colors.light : Colors.white,
              color: currentPage === 1 ? Colors.textLight : Colors.text,
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            ← Prev
          </button>

          {/* Page Numbers */}
          {pageNumbers.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} style={{ padding: '0 4px', color: Colors.textLight }}>
                  ...
                </span>
              );
            }

            const isActive = page === currentPage;
            return (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                style={{
                  padding: '8px 12px',
                  border: `1px solid ${isActive ? Colors.primary : Colors.border}`,
                  borderRadius: '6px',
                  background: isActive ? Colors.primary : Colors.white,
                  color: isActive ? Colors.white : Colors.text,
                  cursor: 'pointer',
                  fontWeight: isActive ? '600' : '500',
                  fontSize: '14px',
                  minWidth: '40px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.target.style.background = Colors.hover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.target.style.background = Colors.white;
                  }
                }}
              >
                {page}
              </button>
            );
          })}

          {/* Next Button */}
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              border: `1px solid ${Colors.border}`,
              borderRadius: '6px',
              background: currentPage === totalPages ? Colors.light : Colors.white,
              color: currentPage === totalPages ? Colors.textLight : Colors.text,
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            Next →
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 8px 0', color: Colors.text, fontSize: '24px', fontWeight: '700' }}>
          Audit Logs
        </h2>
        <p style={{ margin: 0, color: Colors.textLight, fontSize: '14px' }}>
          Track all user activities and system changes • {totalItems} total logs
        </p>
      </div>

      {/* Filters */}
      <div style={{
        background: Colors.white,
        border: `1px solid ${Colors.border}`,
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '20px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: Colors.text }}>
          Filters
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
          {/* User ID Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: Colors.text }}>
              User ID
            </label>
            <input
              type="text"
              placeholder="Enter user ID..."
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${Colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Action Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: Colors.text }}>
              Action
            </label>
            <input
              type="text"
              placeholder="e.g., BOOKING, USER_CREATED"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${Colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Start Date Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: Colors.text }}>
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${Colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* End Date Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: Colors.text }}>
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: `1px solid ${Colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        {/* Filter Buttons */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={handleApplyFilters}
            style={{
              padding: '8px 20px',
              background: Colors.primary,
              color: Colors.white,
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.9'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            Apply Filters
          </button>
          
          {Object.keys(appliedFilters).length > 0 && (
            <button
              onClick={handleClearFilters}
              style={{
                padding: '8px 20px',
                background: Colors.white,
                color: Colors.text,
                border: `1px solid ${Colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = Colors.hover}
              onMouseLeave={(e) => e.target.style.background = Colors.white}
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Active Filters Display */}
        {Object.keys(appliedFilters).length > 0 && (
          <div style={{ marginTop: '12px', padding: '12px', background: Colors.light, borderRadius: '6px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '500', color: Colors.text }}>
              Active Filters:
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(appliedFilters).map(([key, value]) => (
                <span
                  key={key}
                  style={{
                    padding: '4px 8px',
                    background: Colors.white,
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: Colors.text
                  }}
                >
                  <strong>{key}:</strong> {typeof value === 'string' && value.length > 30 ? value.substring(0, 30) + '...' : value}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px',
          background: Colors.white,
          border: `1px solid ${Colors.border}`,
          borderRadius: '8px',
          color: Colors.textLight 
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
          <p style={{ fontSize: '16px', margin: 0 }}>Loading audit logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div style={{
          background: Colors.light,
          border: `1px dashed ${Colors.border}`,
          borderRadius: '8px',
          padding: '60px',
          textAlign: 'center',
          color: Colors.textLight
        }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}><BarChart3Icon size="1em" aria-hidden="true" /></div>
          <p style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: Colors.text }}>
            No audit logs found
          </p>
          <p style={{ margin: 0, fontSize: '14px' }}>
            {Object.keys(appliedFilters).length > 0 
              ? 'Try adjusting your filters to see more results'
              : 'Audit logs will appear here when users perform actions'}
          </p>
        </div>
      ) : (
        <>
          <div style={{ 
            background: Colors.white,
            border: `1px solid ${Colors.border}`,
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: Colors.light, borderBottom: `2px solid ${Colors.border}` }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: Colors.text }}>
                      Action
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: Colors.text }}>
                      User
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: Colors.text }}>
                      Details
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: Colors.text }}>
                      Timestamp
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: Colors.text }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => (
                    <tr 
                      key={log.id} 
                      style={{ 
                        borderBottom: index === logs.length - 1 ? 'none' : `1px solid ${Colors.border}`,
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = Colors.hover}
                      onMouseLeave={(e) => e.currentTarget.style.background = Colors.white}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '20px' }}>{getActionIcon(log.action)}</span>
                          <span style={{ fontWeight: '600', fontSize: '14px', color: Colors.text }}>
                            {log.action}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: Colors.text, marginBottom: '2px' }}>
                            {log.user?.name || 'System'}
                          </div>
                          {log.user?.email && (
                            <div style={{ fontSize: '12px', color: Colors.textLight, marginBottom: '4px' }}>
                              {log.user.email}
                            </div>
                          )}
                          {log.user?.role && (
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              background: getRoleColor(log.user.role),
                              color: Colors.white,
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              {log.user.role}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ maxWidth: '350px', fontSize: '13px', color: Colors.text }}>
                          {formatMetadataPreview(log.metadata)}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: '13px', color: Colors.text }}>
                          {log.createdAt ? format(new Date(log.createdAt), 'MMM dd, yyyy') : 'N/A'}
                        </div>
                        <div style={{ fontSize: '12px', color: Colors.textLight }}>
                          {log.createdAt ? format(new Date(log.createdAt), 'HH:mm:ss') : ''}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleViewDetails(log)}
                          style={{
                            padding: '6px 12px',
                            background: Colors.primary,
                            color: Colors.white,
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = '0.9'}
                          onMouseLeave={(e) => e.target.style.opacity = '1'}
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {renderPagination()}
        </>
      )}

      {/* Metadata Modal */}
      {showMetadataModal && selectedLog && (
        <AuditMetadataModal
          log={selectedLog}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

// Audit Metadata Modal Component
function AuditMetadataModal({ log, onClose }) {
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const toggleKey = (key) => {
    const newExpanded = new Set(expandedKeys);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedKeys(newExpanded);
  };

  const parseMetadata = () => {
    if (!log.metadata) return null;
    
    try {
      return typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata;
    } catch (e) {
      return { raw: String(log.metadata) };
    }
  };

  const metadata = parseMetadata();

  const renderValue = (value, key = '', depth = 0) => {
    if (value === null || value === undefined) {
      return <span style={{ color: Colors.textLight, fontStyle: 'italic' }}>null</span>;
    }

    if (typeof value === 'boolean') {
      return <span style={{ color: value ? Colors.success : Colors.danger, fontWeight: '600' }}>{String(value)}</span>;
    }

    if (typeof value === 'number') {
      return <span style={{ color: Colors.info, fontWeight: '600' }}>{value}</span>;
    }

    if (typeof value === 'string') {
      return <span style={{ color: Colors.text }}>{value}</span>;
    }

    if (Array.isArray(value)) {
      const isExpanded = expandedKeys.has(key);
      return (
        <div>
          <button
            onClick={() => toggleKey(key)}
            style={{
              background: 'none',
              border: 'none',
              color: Colors.primary,
              cursor: 'pointer',
              padding: 0,
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {isExpanded ? '▼' : '►'} Array ({value.length} items)
          </button>
          {isExpanded && (
            <div style={{ marginLeft: '20px', marginTop: '8px' }}>
              {value.map((item, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  <strong>[{index}]:</strong> {renderValue(item, `${key}[${index}]`, depth + 1)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (typeof value === 'object') {
      const isExpanded = expandedKeys.has(key);
      const keys = Object.keys(value);
      return (
        <div>
          <button
            onClick={() => toggleKey(key)}
            style={{
              background: 'none',
              border: 'none',
              color: Colors.primary,
              cursor: 'pointer',
              padding: 0,
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            {isExpanded ? '▼' : '►'} Object ({keys.length} keys)
          </button>
          {isExpanded && (
            <div style={{ marginLeft: '20px', marginTop: '8px' }}>
              {keys.map(k => (
                <div key={k} style={{ marginBottom: '8px' }}>
                  <strong style={{ color: Colors.text }}>{k}:</strong>{' '}
                  {renderValue(value[k], `${key}.${k}`, depth + 1)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: Colors.white,
          borderRadius: '12px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${Colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: Colors.text }}>
            Audit Log Details
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: Colors.textLight,
              padding: 0,
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Basic Info */}
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: Colors.textLight }}>
              BASIC INFORMATION
            </h4>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <strong style={{ color: Colors.text }}>Action:</strong>{' '}
                <span style={{ color: Colors.primary, fontWeight: '600' }}>{log.action}</span>
              </div>
              <div>
                <strong style={{ color: Colors.text }}>User:</strong>{' '}
                {log.user?.name || 'System'} ({log.user?.email || 'N/A'})
              </div>
              {log.user?.role && (
                <div>
                  <strong style={{ color: Colors.text }}>Role:</strong>{' '}
                  <span style={{
                    padding: '2px 8px',
                    background: Colors.primary,
                    color: Colors.white,
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {log.user.role}
                  </span>
                </div>
              )}
              <div>
                <strong style={{ color: Colors.text }}>Timestamp:</strong>{' '}
                {log.createdAt ? format(new Date(log.createdAt), 'MMM dd, yyyy HH:mm:ss') : 'N/A'}
              </div>
              <div>
                <strong style={{ color: Colors.text }}>Log ID:</strong>{' '}
                <code style={{ 
                  background: Colors.light, 
                  padding: '2px 6px', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: Colors.text
                }}>
                  {log.id}
                </code>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: Colors.textLight }}>
              METADATA
            </h4>
            <div style={{
              background: Colors.light,
              border: `1px solid ${Colors.border}`,
              borderRadius: '8px',
              padding: '16px',
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              {metadata ? (
                Object.keys(metadata).map(key => (
                  <div key={key} style={{ marginBottom: '12px' }}>
                    <strong style={{ color: Colors.text }}>{key}:</strong>{' '}
                    {renderValue(metadata[key], key)}
                  </div>
                ))
              ) : (
                <span style={{ color: Colors.textLight, fontStyle: 'italic' }}>No metadata available</span>
              )}
            </div>
          </div>

          {/* Raw JSON */}
          <div style={{ marginTop: '24px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: Colors.textLight }}>
              RAW JSON
            </h4>
            <pre style={{
              background: '#24332A',
              color: '#E3DFD3',
              padding: '16px',
              borderRadius: '8px',
              fontSize: '12px',
              overflow: 'auto',
              maxHeight: '300px',
              margin: 0
            }}>
              {JSON.stringify(metadata || {}, null, 2)}
            </pre>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${Colors.border}`,
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: Colors.primary,
              color: Colors.white,
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.opacity = '0.9'}
            onMouseLeave={(e) => e.target.style.opacity = '1'}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default AuditLogs;
